import { useState } from "react";
import { Mail, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";

export default function Contact() {
  const { toast } = useToast();
  const { t } = useLanguage();
  usePageSEO({
    title: "Contact ISO27001KIT | Support, Sales & Partnership Requests",
    description: "Contact ISO27001KIT for support, payment issues, partnerships, enterprise requests, and ISO 27001 implementation questions.",
    canonicalPath: "/contact",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: t("contact.toast.title"),
      description: t("contact.toast.description"),
    });
    setFormData({
      name: "",
      email: "",
      organization: "",
      subject: "",
      message: "",
    });
  };

  return (
    <div className="min-h-screen py-8 sm:py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">{t("contact.title")}</h1>
        <div className="h-1 w-24 bg-primary mb-6 sm:mb-8"></div>

        <p className="text-muted-foreground text-base sm:text-lg mb-6 sm:mb-8">
          {t("contact.intro")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 bg-card p-4 sm:p-8 rounded-lg border shadow-sm">
          <div className="space-y-2">
            <Label htmlFor="name">{t("contact.form.name")}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t("contact.form.email")}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization">{t("contact.form.organization")}</Label>
            <Input
              id="organization"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">{t("contact.form.subject")}</Label>
            <Select
              value={formData.subject}
              onValueChange={(value) => setFormData({ ...formData, subject: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("contact.form.subjectPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consultation">{t("contact.form.subjectConsultation")}</SelectItem>
                <SelectItem value="customized">{t("contact.form.subjectCustomized")}</SelectItem>
                <SelectItem value="question">{t("contact.form.subjectQuestion")}</SelectItem>
                <SelectItem value="support">{t("contact.form.subjectSupport")}</SelectItem>
                <SelectItem value="other">{t("contact.form.subjectOther")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t("contact.form.message")}</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={6}
              required
            />
          </div>

          <Button type="submit" size="lg" className="w-full">
            <Mail className="mr-2 h-5 w-5" />
            {t("contact.form.submit")}
          </Button>
        </form>

        <div className="mt-8 sm:mt-12 p-6 sm:p-8 bg-accent/10 rounded-lg border border-accent/20">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-foreground">{t("contact.consultation.title")}</h2>
          <p className="text-muted-foreground mb-6 text-sm sm:text-base">
            {t("contact.consultation.description")}
          </p>
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
            <a href="mailto:contact@iso27001kit.com?subject=ISO 27001 Consultation Request&body=Hi, I would like to book a 30-minute consultation to discuss ISO 27001 implementation for my organization.">
              <Mail className="mr-2 h-5 w-5" />
              {t("contact.consultation.button")}
            </a>
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            {t("contact.consultation.email")}{" "}
            <a href="mailto:contact@iso27001kit.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
              contact@iso27001kit.com
            </a>
          </p>
        </div>

        <div className="mt-6 sm:mt-8 text-center p-6 sm:p-8 bg-muted/30 rounded-lg">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-foreground">{t("contact.website.title")}</h2>
          <a
            href="https://iso27001kit.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary underline underline-offset-2 hover:text-primary/80 text-lg"
          >
            iso27001kit.com
            <ExternalLink className="h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  );
}
